// React Component
import React, { useState, useMemo, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

// App Component
import { NewProcessOrGroupV2, UnifiedCard, LibrarySkeletonLoader } from '../../components'
import { AppLayout, HeaderLayout } from '../../layouts'
import { FamlaServices } from '../../services'
import { CompanyAnalysisService } from '../../services/api/companyAnalysisService'
import { listRecentCollectCollections, type RecentCollectCollectionSummary } from '../../services/api/collectDataService'
import { mergeProcessesAndDataCollections, type HomeRecentMergedEntry } from '../../utils/homeRecentMerge'
import { mergeProcessSearchWithDataCollections } from '../../utils/librarySearchMerge'
import { resolveCollectCardOwnerLabel } from '../../utils/collectCardOwnerLabel'
import { role, useAppDispatch, useAppSelector, company } from '../../stores'
import { Spiner } from '../../components'
import { Routes } from '../../routes'
import './process-library.css'
import DataCollectLibraryCard from './DataCollectLibraryCard'
import ProcessLibraryCard from './ProcessLibraryCard'

// Features

//interface
import { iCompany, iProcess } from '../../components/type'
import iProject from '../../interfaces/project'
import { useSearch } from '../../utils'

interface iState {
    isLoadingProcess: boolean,
    isLoadingProject: boolean,
    isLoadingReport: boolean,
    isLoadingApproval: boolean
    isLoading: boolean
    process: any
    myRole: string
    error: string
    popularUseCase: any
    requestApproval: Array<any>
    companyById: iCompany,
    reports: any[]
}


const ProcessView = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const language = localStorage.getItem('currentLanguage') === "fr" ? "french" : "english"
    const users = useAppSelector((state) => state.auth);
    const companyId = users.session.company_id;
    const [visibleProcesses, setVisibleProcesses] = useState(3);
    const [processNumbers, setProcessNumbers] = useState<{ [key: string]: number }>({});
    const [notifProcess, setNotifProcess] = useState<{ [key: string]: boolean }>({});
    const [notifProject, setNotifProject] = useState<{ [key: string]: boolean }>({});
    const [project, setProject] = React.useState<iProject[]>([]);
    const [process, setProcess] = React.useState<iProcess[]>([]);
    const [dataCollections, setDataCollections] = React.useState<RecentCollectCollectionSummary[]>([]);
    const [state, setState] = React.useState<iState>({
        isLoadingProcess: true,
        isLoadingProject: true,
        isLoadingReport: true,
        isLoadingApproval: true,
        isLoading: true,
        process: [],
        myRole: "WORKER",
        error: '',
        popularUseCase: [],
        requestApproval: [],
        companyById: {},
        reports: []
    })
    
    // Refs pour éviter les appels multiples
    const isLoadingReportsRef = React.useRef(false)
    const { searchQuery, setSearchQuery, searchResults, searchLoad, setFilter, filter } = useSearch("/process/search/my/process");
    const safeSearchResults = searchResults ?? [];

    const loadDataCollections = useCallback(async () => {
        if (!users.access_token) return;
        const r = await listRecentCollectCollections(users.access_token, 60);
        if (r.status === 200 && Array.isArray(r.body?.data?.collections)) {
            setDataCollections(r.body.data.collections as RecentCollectCollectionSummary[]);
        }
    }, [users.access_token]);

    const mergedLibraryRecent = useMemo(
        () => mergeProcessesAndDataCollections(process, dataCollections),
        [process, dataCollections],
    );

    const mergedSearchResults = useMemo(
        () =>
            searchQuery
                ? mergeProcessSearchWithDataCollections(
                      safeSearchResults as iProcess[],
                      dataCollections,
                      searchQuery,
                      filter,
                  )
                : [],
        [safeSearchResults, dataCollections, searchQuery, filter],
    );

    React.useEffect(() => {
        void loadDataCollections();
    }, [loadDataCollections]);

    const getProcessNumber = async (projectId: string) => {
        try {
            let data = await FamlaServices("api_host", `/project/number/${projectId}`, "GET", "", users.access_token);
            if (data.status === 200) {
                return data.body.data;
            } else {
                return 0;
            }
        } catch (e) {
            return 0;
        }
    };

    const loadNotificationByProcess = async (processId: string) => {
        try {
            let data = await FamlaServices("api_host", `/notification/by/id/${processId}`, "GET", "", users.access_token);
            if (data.status === 200) {
                return data.body.data;
            } else {
                return false;
            }
        } catch (e) {
            return false;
        }
    };
    const loadNotificationByProject = async (projectId: string) => {
        try {
            let data = await FamlaServices("api_host", `/notification/project/by/id/${projectId}`, "GET", "", users.access_token);
            if (data.status === 200) {
                const datas = data.body.data
                if (datas || datas !== null) {
                    return true
                } else {
                    return false
                }
            } else {
                return false;
            }
        } catch (e) {
            return false;
        }
    };
    const updateProcess = (newData: any) => {
        setProcess(prev =>
            prev.map(item =>
                item._id === newData._id
                    ? {
                        ...item,
                        title: newData.title,
                        description: newData.description,
                        updated_at: newData.updated_at,
                    }
                    : item
            )
        );
    };
    const updateProject = (newData: any) => {
        setProject(prev =>
            prev.map(item =>
                item._id === newData._id
                    ? {
                        ...item,
                        title: newData.title,
                        description: newData.description,
                        updated_at: newData.updated_at,
                    }
                    : item
            )
        );
    };


    React.useEffect(() => {


        const loadCompanyById = async () => {

            try {
                let data = await FamlaServices("api_host", `/company/by/id/${companyId}`, "GET", "", users.access_token);

                if (data.status === 200) {
                    const companie = data.body.data
                    setState((prev) => ({ ...prev, companyById: companie }));
                    let newData = await FamlaServices("api_host", `/request/total_tokens/company/?company_id=${companyId}`, "GET", "", users.access_token);
                    if (newData.status === 200) {
                        const tokenData = newData.body.data
                        const companyData = {
                            ...companie,
                            tokensRemaining: tokenData.left_token,
                            tokensUsed: tokenData.total_tokens,
                            totalTokens: tokenData.total_tokens

                        }
                        dispatch(company({ company: companyData }));
                    }

                } else {

                }

            } catch (error) {

            }

        }

        loadCompanyById()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, users.access_token])


    React.useEffect(() => {
        const updateLang = async () => {
            try {
                const updateData = {
                    fullname: users.user.fullname,
                    phone_number: users.user.phone_number,
                    lang: language,
                    updated_at: users.user.created_at
                };

                let data = await FamlaServices("api_host", `/users/`, "PUT", updateData, users.access_token);
                if (data.status === 200) {
                }
            } catch (error) {

            }

        }
        updateLang()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users.access_token, language])


    React.useEffect(() => {

        const getMyRole = async () => {
            try {
                let data = await FamlaServices("api_host", `/users/my/role`, "GET", "", users.access_token);
                if (data.status === 200) {
                    setState((prev) => ({ ...prev, myRole: data.body.data }))
                    dispatch(role({ role: data.body.data }),
                    )
                }
            } catch (error) {

            }

        }
        getMyRole()

        const getProcessUser = async () => {
            try {
                const data = await FamlaServices("api_host", `/process/my`, "GET", "", users.access_token);

                if (data.status === 200) {
                    const processes = data.body.data;

                    const notif: { [key: string]: boolean } = {};
                    for (const process of processes.slice(0, 3)) {
                        notif[process._id] = await loadNotificationByProcess(process._id);
                    }

                    setState((prev) => ({ ...prev, isLoadingProcess: false }));

                    setProcess((prevProcesses) => {
                        const processMap = new Map(prevProcesses.map((p) => [p._id, p]));
                        processes.forEach((newProcess: iProcess) => {
                            if (!processMap.has(newProcess._id)) {
                                processMap.set(newProcess._id, newProcess);
                            }
                        });

                        return Array.from(processMap.values());
                    });

                    setNotifProcess(notif);
                    void loadDataCollections();
                }
            } catch (error) {

            }
        };

        getProcessUser();

        const getFolderByUser = async () => {
            try {
                let data = await FamlaServices("api_host", `/project/my`, "GET", "", users.access_token);

                if (data.status === 200) {
                    let projects = data.body.data
                    const numbers: { [key: string]: number } = {};
                    const notif: { [key: string]: boolean } = {};
                    for (const project of projects.slice(0, 3)) {
                        numbers[project._id] = await getProcessNumber(project._id);
                        notif[project._id] = await loadNotificationByProject(project._id)
                    }
                    setProcessNumbers(numbers);
                    setNotifProject(notif)
                    setProject(projects)
                    setState((prev) => ({ ...prev, isLoadingProject: false }))
                }
            } catch (error) {

            }
        }

        getFolderByUser()

        const loadReportsAnalysis = async () => {
            if (isLoadingReportsRef.current || !companyId || !users.access_token) return

            isLoadingReportsRef.current = true
            try {
                const analyses = await CompanyAnalysisService.getCompanyRecentAnalyses(
                    companyId,
                    users.access_token,
                )
                setState((prev) => ({ ...prev, reports: analyses, isLoadingReport: false }))
            } catch (error: any) {
                setState((prev) => ({ ...prev, reports: [], isLoadingReport: false, error: error.message }))
            } finally {
                setTimeout(() => {
                    isLoadingReportsRef.current = false
                }, 300)
            }
        }

        loadReportsAnalysis();

        const loadApprovalList = async () => {
            try {
                let data = await FamlaServices("api_host", `/approval/`, "GET", "", users.access_token);
                if (data.status === 200) {
                    setState((prev) => ({ ...prev, requestApproval: data.body.data, isLoadingApproval: false }));
                } else {
                    setState((prev) => ({ ...prev, isLoadingApproval: false, error: 'Failed to fetch data' }));
                }
            } catch (error: any) {
                setState((prev) => ({ ...prev, isLoadingApproval: false }));
            }
        }

        loadApprovalList();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users.access_token])

    React.useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 576) {
                setVisibleProcesses(3);
            } else if (window.innerWidth < 768) {
                setVisibleProcesses(4)
            } else if (window.innerWidth < 1540) {
                setVisibleProcesses(4);
            } else if (window.innerWidth < 1837) {
                setVisibleProcesses(5);
            } else if (window.innerWidth < 2130) {
                setVisibleProcesses(6);
            } else if (window.innerWidth < 2424) {
                setVisibleProcesses(7);
            } else if (window.innerWidth < 3016) {
                setVisibleProcesses(8);
            } else {
                setVisibleProcesses(10);
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => window.removeEventListener('resize', handleResize);
    }, [state.myRole]);

    const deleProcess = async (process_id: string, _name?: string) => {
        setProcess(prevState =>
            prevState.filter((process: { _id: string }) => process._id !== process_id)
        );
        await FamlaServices("api_host", `/process/${process_id}`, "DELETE", "", users.access_token);
    }

    const deleFolder = async (folder_id: string, name: string) => {
        if (name !== "Default") {
            setProject(project.filter((project: { _id: string }) => project._id !== folder_id));
            await FamlaServices("api_host", `/project/${folder_id}`, "DELETE", "", users.access_token);
        }
    }

    const renderProcessLibraryRecentCard = (proc: iProcess, index: number) => (
        <ProcessLibraryCard
            key={proc._id}
            proc={proc}
            index={index}
            hasNotif={notifProcess[proc._id] || false}
            ownerLabel={
                users.user.fullname === proc.owner.fullname ? t('words.me') : proc.owner.fullname
            }
            deleProcess={deleProcess}
            updateProcess={updateProcess}
            updateProject={updateProject}
        />
    );

    const renderMergedLibraryRecentEntry = (entry: HomeRecentMergedEntry, index: number) => {
        if (entry.kind === 'process') {
            return renderProcessLibraryRecentCard(entry.item, index);
        }
        const dcOwnerLabel = resolveCollectCardOwnerLabel(entry.item, t, {
            userId: users.user._id || users.session.user._id,
            userFullname: users.user.fullname,
        })
        return (
            <DataCollectLibraryCard
                key={`dc-${entry.item.id}`}
                col={entry.item}
                index={index}
                ownerLabel={dcOwnerLabel}
                accessToken={users.access_token}
                onRefresh={() => void loadDataCollections()}
            />
        );
    };

    return (
        <AppLayout>
            <>
                <HeaderLayout
                    loadcompany={true}
                    listApproval={state.requestApproval}
                    loading={state.isLoadingApproval}
                    companyById={state.companyById}
                    key={'processView'}
                    inputValue={searchQuery}
                    setSearchQuery={setSearchQuery}
                    setFilter={setFilter}
                    search={true}
                    filterValue={filter}
                    searchfilter={true}

                />

                {(state.isLoadingProcess || state.isLoadingProject) ?
                    <div className="library-layout-root">
                        <div className="library-page-shell overflow-auto">
                            <div className="library-dashboard">
                                <div className="library-section-header">
                                    <div className="library-section-title">{t('views.processView.libraryRecentProcesses')}</div>
                                </div>
                                <div className="library-grid-4">
                                    <LibrarySkeletonLoader count={4} variant="process" />
                                </div>
                                <div className="library-section-header">
                                    <div className="library-section-title">{t('views.processView.folders')}</div>
                                </div>
                                <div className="library-grid-4">
                                    <LibrarySkeletonLoader count={4} variant="folder" />
                                </div>
                                <div className="library-section-header">
                                    <div className="library-section-title">{t('views.processView.libraryRecentInsights')}</div>
                                </div>
                                <div className="lib-insights-grid">
                                    <LibrarySkeletonLoader count={4} variant="insight" />
                                </div>
                            </div>
                        </div>
                    </div>
                    :
                    <div className="library-layout-root">
                        <div className="library-page-shell overflow-auto">
                            {searchQuery !== '' ? (
                                <div className="library-dashboard">
                                    <div className="library-section-header library-search-block" style={{ marginBottom: 0 }}>
                                        <div className="library-section-title">{`${t('words.searching')}… ${searchQuery}`}</div>
                                    </div>
                                    <div className="library-grid-4">
                                        {mergedSearchResults.slice(0, visibleProcesses).map((entry, index) =>
                                            renderMergedLibraryRecentEntry(entry, index),
                                        )}
                                    </div>
                                    {!searchLoad && mergedSearchResults.length === 0 && (
                                        <NewProcessOrGroupV2
                                            newElement={t('views.processView.processNotFound')}
                                            create="process"
                                            noResult={true}
                                        />
                                    )}
                                </div>
                            ) : (
                                <div className="library-dashboard">
                                    <div className="library-section-header">
                                        <div className="library-section-title">{t('views.processView.libraryRecentProcesses')}</div>
                                        {mergedLibraryRecent.length > 4 && (
                                            <button
                                                type="button"
                                                className="library-section-link"
                                                onClick={() => navigate(Routes.MY_PROCESS)}
                                            >
                                                {t('views.processView.viewAll')}
                                            </button>
                                        )}
                                    </div>
                                    <div className="library-grid-4">
                                        {mergedLibraryRecent.slice(0, 4).map((entry, index) =>
                                            renderMergedLibraryRecentEntry(entry, index),
                                        )}
                                    </div>
                                    {!state.isLoadingProcess && mergedLibraryRecent.length === 0 && (
                                        <div className="lib-insights-empty" style={{ marginBottom: 24 }}>
                                            {t('views.processView.processNotFound')}
                                        </div>
                                    )}

                                    <div className="library-section-header">
                                        <div className="library-section-title">{t('views.processView.folders')}</div>
                                        {project.length > 4 && (
                                            <button
                                                type="button"
                                                className="library-section-link"
                                                onClick={() => navigate(Routes.ALL_GROUP)}
                                            >
                                                {t('views.processView.viewAll')}
                                            </button>
                                        )}
                                    </div>
                                    <div className="library-grid-4">
                                        <Link to={Routes.CREATE_GROUP_PROCESS} className="lib-folder-new-card">
                                            <svg width={20} height={20} viewBox="0 0 16 16" fill="none" aria-hidden>
                                                <path
                                                    d="M8 2V14M2 8H14"
                                                    stroke="currentColor"
                                                    strokeWidth="1.3"
                                                    strokeLinecap="round"
                                                />
                                            </svg>
                                            <span className="lib-folder-new-label">{t('views.processView.libraryNewFolder')}</span>
                                        </Link>
                                        {state.isLoadingProject ? (
                                            <div
                                                className="d-flex align-items-center justify-content-center py-4"
                                                style={{ gridColumn: '2 / -1' }}
                                            >
                                                <Spiner className="spinner-border-sm" />
                                            </div>
                                        ) : (
                                            project?.map((proj: iProject, index: number) => (
                                                <UnifiedCard
                                                    key={proj._id}
                                                    variant="library"
                                                    processName={proj.title}
                                                    thisProcessHasABadge={processNumbers[proj._id] > 0}
                                                    theBadgeHasAValue={true}
                                                    badgeValue={processNumbers[proj._id] || 0}
                                                    thisProcessHasAOwner={true}
                                                    owner=""
                                                    route=""
                                                    deleteProcess={() => deleFolder(proj._id, proj.title)}
                                                    description={proj.description}
                                                    processId={proj._id}
                                                    folder={true}
                                                    setProject={updateProject}
                                                    setProcess={updateProcess}
                                                    elementType="folder"
                                                    notifFolder={notifProject[proj._id] || false}
                                                    index={index}
                                                    t={t}
                                                />
                                            ))
                                        )}
                                    </div>
                                    <div className="library-section-header">
                                        <div className="library-section-title">{t('views.processView.libraryRecentInsights')}</div>
                                        {Array.isArray(state.reports) && state.reports.length > 4 && (
                                            <button
                                                type="button"
                                                className="library-section-link"
                                                onClick={() => navigate(Routes.COMPANYANALYSIS)}
                                            >
                                                {t('views.processView.viewAll')}
                                            </button>
                                        )}
                                    </div>
                                    {state.isLoadingReport ? (
                                        <Spiner className="ms-2 mt-2 spinner-border-sm" />
                                    ) : (
                                        <div className="lib-insights-grid">
                                            {Array.isArray(state.reports) && state.reports.length === 0 ? (
                                                <div className="lib-insights-empty">
                                                    <svg width={18} height={18} viewBox="0 0 16 16" fill="none" aria-hidden>
                                                        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.2" />
                                                        <path
                                                            d="M5 8H11M8 5V11"
                                                            stroke="currentColor"
                                                            strokeWidth="1.2"
                                                            strokeLinecap="round"
                                                        />
                                                    </svg>
                                                    <span>{t('views.processView.libraryNoInsights')}</span>
                                                </div>
                                            ) : (
                                                Array.isArray(state.reports) &&
                                                state.reports.slice(0, 4).map((analysis: any) => {
                                                    const analysisId =
                                                        analysis.analysis_id || analysis._id || analysis.id
                                                    return (
                                                    <UnifiedCard
                                                        key={analysisId}
                                                        variant="library"
                                                        processName={analysis.name || 'Insight'}
                                                        thisProcessHasABadge={false}
                                                        theBadgeHasAValue={false}
                                                        badgeValue={0}
                                                        thisProcessHasAOwner={false}
                                                        owner=""
                                                        route={`${Routes.COMPANYANALYSIS}/${analysisId}`}
                                                        deleteProcess={() => {}}
                                                        description={analysis.process_name || ''}
                                                        processId=""
                                                        folder={false}
                                                        setProject={() => {}}
                                                        usage="populare"
                                                        setProcess={() => {}}
                                                        elementType="popular"
                                                        t={t}
                                                    />
                                                    )
                                                })
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                }

            </>
        </AppLayout>
    )
}

export default ProcessView